import { useCallback, useRef } from 'react';

import { useLatest } from '#/hooks/useLatest';

/*
  useDebouncedFn: Hook to provide a render-stable debounced version
  of the passed function

  Does not (yet) support "leading" debounce option

  Returns: debounced function
*/

export function useDebouncedFn<TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  delay: number,
) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fnRef = useLatest(fn);
  const delayRef = useLatest(delay);

  const debuncedFn = useCallback(
    (...args: TArgs) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(
        () => fnRef.current(...args),
        delayRef.current,
      );
    },
    [fnRef, delayRef],
  );

  return debuncedFn;
}
